import React, {useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {Descriptions, Divider, Typography} from 'antd'

import {useScreenType} from '@/hooks'
import {withTitle} from '@/router/utils'
import {Container, PageContainer} from '@/components'
import {getPermissions, navigateUserAfterLogin} from '@/stores/auth/services'

import {ICompanyProfileStore} from './ICompanyProfileStore'
import styles from './CompanyProfile.scss'
import { CUSTOMER_ID } from '@/constants/playwright-ids'

export const CompanyProfileStoreSymbol = Symbol('CompanyProfileStoreSymbol')

const CompanyProfile: React.FC<null> = observer(() => {
    const { t } = useTranslation()
    const {companyInfo} = useInjection<ICompanyProfileStore>(CompanyProfileStoreSymbol)
    const {isMobile} = useScreenType()

    useEffect(() => {
        if (!companyInfo) {
            navigateUserAfterLogin(getPermissions())
        }
    }, [companyInfo])

    return (
        <Container className={styles.container}>
            <PageContainer title={t('CompanyProfile.companyProfile')} isLoading={!!!companyInfo}>
                <Descriptions
                    className={classNames(styles.descriptionLabel, {
                        [styles.descriptionLabelMobile]: isMobile
                    })}
                    title=''
                    layout={isMobile ? 'vertical' : 'horizontal'}
                    column={1}
                    colon={false}
                >
                    <Descriptions.Item label={t('CompanyProfile.labels.customerId')}>
                        <Typography.Paragraph data-test-id={CUSTOMER_ID} className={styles.customerId} copyable>
                            {companyInfo?.dsrId}
                        </Typography.Paragraph>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('CompanyProfile.labels.entityType')}>
                        {companyInfo?.businessStructureValue}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('CompanyProfile.labels.registeredNumber')}>
                        {companyInfo?.registeredNumber}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('CompanyProfile.labels.legalName')}>
                        {companyInfo?.companyName}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('CompanyProfile.labels.registeredAddress')}>
                        {companyInfo?.registeredAddress}
                    </Descriptions.Item>
                </Descriptions>
                {(companyInfo?.tradingName || companyInfo?.tradingAddress) && (
                    <Divider className={styles.divider} />
                )}
                <Descriptions
                    className={classNames(styles.descriptionLabel, {
                        [styles.descriptionLabelMobile]: isMobile
                    })}
                    title=''
                    layout={isMobile ? 'vertical' : 'horizontal'}
                    column={1}
                    colon={false}
                >
                    {companyInfo?.commercialName && (
                        <Descriptions.Item label={t('CompanyProfile.labels.tradingName')}>
                            {companyInfo?.commercialName}
                        </Descriptions.Item>
                    )}
                    {companyInfo?.tradingAddress && (
                        <Descriptions.Item label={t('CompanyProfile.labels.tradingAddress')}>
                            {companyInfo?.tradingAddress}
                        </Descriptions.Item>
                    )}
                </Descriptions>
                <Divider className={styles.divider} />
                <Descriptions
                    className={classNames(styles.descriptionLabel, {
                        [styles.descriptionLabelMobile]: isMobile
                    })}
                    title=''
                    layout={isMobile ? 'vertical' : 'horizontal'}
                    column={1}
                    colon={false}
                >
                    {companyInfo?.companyWebSite && (
                        <Descriptions.Item label={t('CompanyProfile.labels.companyWebsite')}>
                            <a href={companyInfo?.companyWebSite}>{companyInfo?.companyWebSite}</a>
                        </Descriptions.Item>
                    )}
                    <Descriptions.Item label={t('CompanyProfile.labels.companyEmailAddress')}>
                        {companyInfo?.companyEmailAddress}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('CompanyProfile.labels.companyPhoneNumber')}>
                        {companyInfo?.companyPhoneNumber}
                    </Descriptions.Item>
                </Descriptions>
                <Divider className={styles.divider} />
            </PageContainer>
        </Container>
    )
})

export default withTitle(CompanyProfile, 'CompanyProfile.companyProfile')
